/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import template from './line.stache';
import {
    getFieldName,
    MAKE_CUT_PROBABILITY,
    WIN_PROBABILITY,
} from 'components/probability/types';
import _ from 'lodash';
import dtm from 'scripts/services/dtm';

function checkDiff(prev, now) {
    if (now - prev > 0) {
        return 1;
    }

    if (now - prev < 0) {
        return -1;
    }

    return 0;
}

export function getDiffClass(diff) {
    if (diff === 1) {
        return 'arrow-up';
    }

    if (diff === -1) {
        return 'arrow-down';
    }

    return '';
}

Component.extend({
    tag: 'biggest-movers-line',
    template: template,
    viewModel: {
        define: {
            activeId: {
                type: 'string',
            },
            player: {
                type: '*',
            },
            round: {
                type: 'number',
            },
            currentProbability: {
                get() {
                    const probability = this.attr('probabilities');

                    if (!probability) {
                        return null;
                    }

                    const currentProbability =
                        probability[this.attr('probabilityField')];

                    if (!currentProbability) {
                        return null;
                    }

                    return _.find(
                        currentProbability,
                        (it) => it.roundNumber === this.attr('round'),
                    );
                },
            },
            probability: {
                type: 'array',
                value: [],
            },
            probabilityField: {
                type: 'string',
            },
            probabilityType: {
                type: 'string',
            },
            probabilities: {
                set(value) {
                    return Object.assign(value, {
                        makeCutRounds: value.makeCutRounds.map((it) =>
                            Object.assign(it, {
                                diff: {
                                    value: it.now - it.prev,
                                    better: checkDiff(it.prev, it.now),
                                },
                            }),
                        ),
                        top10Rounds: value.top10Rounds.map((it) =>
                            Object.assign(it, {
                                diff: {
                                    value: it.now - it.prev,
                                    better: checkDiff(it.prev, it.now),
                                },
                            }),
                        ),
                        winProbabilityRounds: value.winProbabilityRounds.map(
                            (it) =>
                                Object.assign(it, {
                                    diff: {
                                        value: it.now - it.prev,
                                        better: checkDiff(it.prev, it.now),
                                    },
                                }),
                        ),
                    });
                },
            },
            defaultProbabilityType: {
                get() {
                    return this.probabilities.dayNumber > 2
                        ? WIN_PROBABILITY
                        : MAKE_CUT_PROBABILITY;
                },
            },
        },
        setActiveId(player) {
            if (this.attr('activeId') === player.identifier) {
                this.attr('activeId', null);
                return;
            }
            dtm.trackBiggestMoversClick(
                player.firstName + ' ' + player.lastName,
            );
            this.attr('activeId', player.identifier);
        },
        getDiffClass,
        processDisplayValue(value) {
            if (_.isNil(value)) {
                return '--';
            }

            return Number(value * 100).toFixed(1) + '%';
        },
    },
    events: {
        inserted() {
            this.registerModelListeners();

            this.viewModel.attr(
                'probabilityField',
                getFieldName(this.viewModel.attr('probabilityType')),
            );
        },

        registerModelListeners() {
            this.viewModel.bind('probabilityType', (__, value) => {
                this.viewModel.attr('probabilityField', getFieldName(value));
            });
        },
    },
});
